import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ShieldCheck, Check, X, Trash2, Clock, Loader2, Mail } from "lucide-react";
import { ScrollToTop } from "../components/ScrollToTop";
import { api } from "../lib/api";

const STATUS = {
  pending: { label: "Wacht op goedkeuring", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  approved: { label: "Goedgekeurd", cls: "bg-green-100 text-green-800 border-green-200" },
  rejected: { label: "Afgewezen", cls: "bg-red-100 text-red-700 border-red-200" },
};

export default function Admin() {
  const [users, setUsers] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      setUsers(await api.admin.listUsers());
    } catch {
      toast.error("Kon gebruikers niet laden");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id, fn, msg) => {
    setBusy(id);
    try {
      await fn(id);
      toast.success(msg);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Actie mislukt");
    } finally {
      setBusy("");
    }
  };

  const pending = (users || []).filter((u) => u.status === "pending");
  const others = (users || []).filter((u) => u.status !== "pending");

  return (
    <main className="mx-auto max-w-4xl px-5 py-8 sm:px-8" data-testid="admin-page">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--brand)] text-white">
          <ShieldCheck className="h-5 w-5" />
        </div>
        <div>
          <h1 className="font-serif text-2xl font-bold text-[var(--ink)]">Beheer</h1>
          <p className="text-sm text-[var(--ink-3)]">Keur aanmeldingen goed of weiger ze.</p>
        </div>
      </div>

      {users === null ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[var(--brand)]" /></div>
      ) : (
        <>
          <section className="mb-8">
            <h2 className="mb-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-[var(--ink-3)]">
              <Clock className="h-3.5 w-3.5" /> In afwachting ({pending.length})
            </h2>
            {pending.length === 0 ? (
              <p className="rounded-xl border border-dashed border-[var(--border-subtle)] px-4 py-6 text-center text-sm text-[var(--ink-3)]" data-testid="admin-no-pending">
                Geen openstaande aanmeldingen.
              </p>
            ) : (
              <div className="space-y-2">
                {pending.map((u) => (
                  <div key={u.id} data-testid={`admin-pending-${u.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[var(--border-subtle)] bg-[var(--surface)] px-4 py-3">
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-[var(--ink)]">{u.name || "—"}</p>
                      <p className="flex items-center gap-1 truncate text-sm text-[var(--ink-3)]"><Mail className="h-3 w-3" />{u.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => act(u.id, api.admin.approve, "Gebruiker goedgekeurd — er is een e-mail verstuurd")}
                        disabled={busy === u.id}
                        data-testid={`admin-approve-${u.id}`}
                        className="flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                      >
                        <Check className="h-3.5 w-3.5" /> Goedkeuren
                      </button>
                      <button
                        onClick={() => act(u.id, api.admin.reject, "Gebruiker afgewezen")}
                        disabled={busy === u.id}
                        data-testid={`admin-reject-${u.id}`}
                        className="flex items-center gap-1.5 rounded-lg border border-[var(--border-subtle)] px-3 py-1.5 text-xs font-semibold text-[var(--ink-2)] transition-colors hover:border-red-300 hover:text-red-600 disabled:opacity-60"
                      >
                        <X className="h-3.5 w-3.5" /> Weigeren
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section>
            <h2 className="mb-3 font-mono text-[11px] uppercase tracking-wider text-[var(--ink-3)]">Alle accounts ({others.length})</h2>
            <div className="space-y-2">
              {others.map((u) => {
                const st = STATUS[u.status] || STATUS.pending;
                return (
                  <div key={u.id} data-testid={`admin-user-${u.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[var(--border-subtle)] bg-[var(--surface)] px-4 py-3">
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-[var(--ink)]">
                        {u.name || "—"}
                        {u.role === "admin" && <span className="ml-2 rounded bg-[var(--brand)]/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-[var(--brand)]">admin</span>}
                      </p>
                      <p className="flex items-center gap-1 truncate text-sm text-[var(--ink-3)]"><Mail className="h-3 w-3" />{u.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`rounded-full border px-2.5 py-1 text-[11px] font-medium ${st.cls}`}>{st.label}</span>
                      {u.role !== "admin" && (
                        <>
                          {u.status !== "approved" && (
                            <button onClick={() => act(u.id, api.admin.approve, "Gebruiker goedgekeurd")} disabled={busy === u.id} data-testid={`admin-approve2-${u.id}`} className="rounded-lg bg-green-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-60">Goedkeuren</button>
                          )}
                          <button onClick={() => act(u.id, api.admin.deleteUser, "Gebruiker verwijderd")} disabled={busy === u.id} data-testid={`admin-delete-${u.id}`} title="Verwijderen" className="flex h-8 w-8 items-center justify-center rounded-lg border border-[var(--border-subtle)] text-[var(--ink-3)] transition-colors hover:border-red-300 hover:text-red-600 disabled:opacity-60">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </>
      )}
      <ScrollToTop />
    </main>
  );
}
